import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
type NavItem = {
  label: string;
  sectionId?: string;
  to?: string;
};
const NAV_ITEMS: NavItem[] = [{
  label: 'Philosophy',
  sectionId: 'philosophy'
}, {
  label: 'Offerings',
  sectionId: 'offerings'
}, {
  label: 'Schedule',
  sectionId: 'schedule'
}, {
  label: 'Connect',
  sectionId: 'connect'
}, {
  label: 'Give Back',
  to: '/give-back'
}, {
  label: 'About',
  to: '/about'
}];
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  const goToSection = (sectionId: string) => {
    setMenuOpen(false);
    if (isHome) {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      navigate('/', {
        state: {
          scrollTo: sectionId
        }
      });
    }
  };

  // Pages whose hero art is pale need the overlaid nav drawn in ink instead of white.
  const hasLightHero = location.pathname === '/give-back' || location.pathname === '/about';
  const onInk = scrolled || hasLightHero;
  const linkClass = ['text-[11px] font-light uppercase tracking-ultra transition-colors duration-300', onInk ? 'text-ink/70 hover:text-clay' : 'text-white/85 hover:text-white'].join(' ');
  return <header className={['fixed inset-x-0 top-0 z-50 transition-all duration-700', scrolled ? 'bg-cream/90 backdrop-blur-md shadow-[0_1px_0_rgba(180,115,107,0.14)]' : 'bg-transparent'].join(' ')} data-id="element-48" data-mp="3tali2wiylcdo">
      <div className="mx-auto flex h-20 max-w-[1400px] items-center justify-between px-6 md:px-10" data-id="element-49" data-mp="1wbuwkl1bm6hy">
        <Link to="/" className={['font-display text-xl tracking-ultra transition-colors duration-500', onInk ? 'text-ink' : 'text-white'].join(' ')} data-id="element-50" data-mp="1hni1rrdyytp1">
          PARṆA
        </Link>

        <nav aria-label="Primary" className="hidden md:block" data-id="element-51" data-mp="1cp6qrdbxud58">
          <ul className="flex items-center gap-6 lg:gap-9" data-id="element-52" data-mp="1cmc55ih6uma8">
            {NAV_ITEMS.map(item => <li key={item.label} data-id="element-53" data-mp="yr8o356zc55k">
                {item.to ? <Link to={item.to} className={linkClass} data-id="element-54" data-mp="13oimhaccjr5j">
                    {item.label}
                  </Link> : <button type="button" onClick={() => goToSection(item.sectionId as string)} className={linkClass} data-id="element-55" data-mp="2y200o6qv3bnw">
                    {item.label}
                  </button>}
              </li>)}
          </ul>
        </nav>

        <div className="flex items-center gap-3" data-id="element-56" data-mp="w4psfsmt2pyr">
          <button type="button" onClick={() => goToSection('connect')} className={['hidden rounded-full border px-6 py-3 text-[10px] font-light uppercase tracking-ultra transition-all duration-500 lg:inline-block', onInk ? 'border-clay/40 text-clay hover:bg-clay hover:text-cream' : 'border-white/60 text-white hover:bg-white/15'].join(' ')} data-id="element-57" data-mp="w6kdd6y2ui2u">
            Begin your journey
          </button>

          <button type="button" onClick={() => setMenuOpen(true)} aria-label="Open menu" className={['md:hidden', onInk ? 'text-ink' : 'text-white'].join(' ')} data-id="element-58" data-mp="3gup1y6ji173h">
            <MenuIcon className="h-6 w-6" strokeWidth={1.25} data-id="element-59" data-mp="22z6xgm4kgpke" />
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-60" data-mp="27wywe03mpby3">
        {menuOpen && <motion.div className="fixed inset-0 z-50 bg-cream px-6 py-6 md:hidden" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.35
      }} data-id="element-61" data-mp="27zuv3qnaw4nl">
            <div className="flex h-14 items-center justify-between" data-id="element-62" data-mp="1k98mny9dwid2">
              <span className="font-display text-xl tracking-ultra text-ink" data-id="element-63" data-mp="1p70opl3qtkuz">PARṆA</span>
              <button type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu" className="text-ink" data-id="element-64" data-mp="1t8vrku1bo6av">
                <XIcon className="h-6 w-6" strokeWidth={1.25} data-id="element-65" data-mp="11ho6jvqfu2gj" />
              </button>
            </div>
            <nav aria-label="Mobile" className="mt-16" data-id="element-66" data-mp="15j9ovwlqr8y4">
              <ul className="space-y-8" data-id="element-67" data-mp="15hqbqevdte0k">
                {NAV_ITEMS.map((item, i) => <motion.li key={item.label} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.08 * i + 0.1,
              duration: 0.5
            }} data-id="element-68" data-mp="xygygajr8v68" data-mpi={i}>
                    {item.to ? <Link to={item.to} onClick={() => setMenuOpen(false)} className="font-display text-4xl font-light text-ink" data-id="element-69" data-mp="jz7ohsm56xap">
                        {item.label}
                      </Link> : <button type="button" onClick={() => goToSection(item.sectionId as string)} className="font-display text-4xl font-light text-ink" data-id="element-70" data-mp="5bcri013q6rt">
                        {item.label}
                      </button>}
                  </motion.li>)}
              </ul>
            </nav>
            <button type="button" onClick={() => goToSection('connect')} className="mt-16 inline-block rounded-full border border-clay/40 px-8 py-4 text-[10px] uppercase tracking-ultra text-clay" data-id="element-71" data-mp="3dvgfjjmdbljy">
              Begin your journey
            </button>
          </motion.div>}
      </AnimatePresence>
    </header>;
}